/* eslint-disable max-lines -- one route table: the customer app routes */
import { BookingDetailsTabs, BookingsListTabs, TireOfferTabs } from './customerRouteParams';
import { defineRoutes } from './types';
import { getUrlWithQueryParams } from './utils';

const HOME_PATH = '/home';
const NEW_BOOKING_PATH = '/bookings/new';
const STATIONARY_BOOKING_PATH = '/bookings/service-center';
const TIRE_OFFER_PATH = '/tire-offer';
const BOOKING_PROPOSAL_PATH = '/p';

type StringOrNumber = string | number;

// Define the parameters for the routes
interface PathConfig {
  expandBookingSummary: { id: StringOrNumber };
  editMyBookingTimeWindow: { slug: StringOrNumber };
  editBookingAddress: { slug: StringOrNumber };
  myCarDetails: { carId: StringOrNumber };
  myCarWheelMeasurements: { carId: StringOrNumber };
  paymentInfo: { slug: StringOrNumber };
  referralsInfo: { slug: StringOrNumber };
  campaignFromCode: { code: string };
  campaignFromCodeLegacy: { code: string };
  bookingInfo: { slug: StringOrNumber; tab?: BookingDetailsTabs };
  bookingUpdateOffer: { slug: StringOrNumber };
  bookings: { tab?: BookingsListTabs };
  changeBooking: { slug: StringOrNumber };
  confirmation: { slug: StringOrNumber };
  membership: { slug: StringOrNumber };
  tireOfferInfo: { slug: StringOrNumber };
  tireOfferSelection: { slug: StringOrNumber };
  tireOfferAddress: { slug: StringOrNumber };
  tireOfferLogin: { slug: StringOrNumber };
  tireOfferSummary: { slug: StringOrNumber };
  tireOfferConfirmation: { sessionId: string };
  tireOfferPaymentInfo: { sessionId: string };
  newStationaryBookingPayForBooking: { sessionId: string };
  tireOfferStatus: { slug: StringOrNumber; tab?: TireOfferTabs };
  bookingProposalLandingPage: { slug: StringOrNumber };
  bookingProposalAddress: { slug: StringOrNumber };
  bookingProposalTimeWindowPicker: { slug: StringOrNumber };
  bookingProposalLogin: { slug: StringOrNumber };
  bookingProposalConfirmation: { slug: StringOrNumber };
  bookingProposalBuilding: { slug: StringOrNumber };
  stationaryProposalTimeWindow: { slug: StringOrNumber };
  stationaryProposalLogin: { slug: StringOrNumber };
  stationaryProposalSummary: { slug: StringOrNumber };
  bookingProposal: { slug: StringOrNumber };
  editProposalAddress: { slug: StringOrNumber };
  payForBooking: { sessionId: string; countryCode?: string };
  feedbackForBooking: { slug: StringOrNumber };
  unableToCompleteDetails: { slug: StringOrNumber };
  feedbackSuccess: { slug: StringOrNumber };
  tireOfferInvoicedOrderConfirmed: { slug: StringOrNumber };
  pointOfSalePayment: { serviceDepartmentId: StringOrNumber };
  storageTransferPublic: { slug: string };
  storageFacilityTransfers: { publicSlug: string };
  tireHotelRehomePublic: { slug: string };
  fleetCalendarPublic: { departmentSlug: string };
  brandInsights: { insightsToken: string };
}

export const customerRoutes = defineRoutes<PathConfig>()({
  feedbackSuccess: {
    getPath: ({ slug }) => `/feedback-success/${slug}`,
    getBasePath: () => `/feedback-success/:slug`
  },
  userSelection: {
    getPath: () => `/user-selection`,
    getBasePath: () => '/user-selection'
  },
  login: {
    getPath: () => '/login',
    getBasePath: () => '/login'
  },
  emailLogin: {
    getPath: () => '/login/email',
    getBasePath: () => '/login/email'
  },
  resetPassword: {
    getPath: () => '/reset-password',
    getBasePath: () => '/reset-password'
  },
  homepage: {
    getPath: () => `/homepage`,
    getBasePath: () => '/homepage'
  },
  home: {
    getPath: () => `${HOME_PATH}`,
    getBasePath: () => `${HOME_PATH}/*`
  },
  tireOfferInfo: {
    getPath: ({ slug, ...rest }) =>
      getUrlWithQueryParams({
        path: `${TIRE_OFFER_PATH}/info/${slug}`,
        queryParams: rest
      }),
    getBasePath: () => `/info/:slug`
  },
  myCarDetails: {
    getPath: ({ carId }) => `${HOME_PATH}/cars/${carId}`,
    getBasePath: () => '/cars/:carId'
  },
  myCarWheelMeasurements: {
    getPath: ({ carId }) => `${HOME_PATH}/cars/${carId}/wheel-measurements`,
    getBasePath: () => '/cars/:carId/wheel-measurements'
  },
  myCars: {
    getPath: () => `${HOME_PATH}/cars`,
    getBasePath: () => '/cars'
  },
  discounts: {
    getPath: () => `${HOME_PATH}/discounts`,
    getBasePath: () => '/discounts'
  },
  tireOfferHomePage: {
    getPath: () => `${HOME_PATH}/tire-offer`,
    getBasePath: () => '/tire-offer'
  },
  bookingProposals: {
    getPath: () => `${HOME_PATH}/booking-proposals`,
    getBasePath: () => '/booking-proposals'
  },
  profile: {
    getPath: () => `${HOME_PATH}/profile`,
    getBasePath: () => '/profile'
  },
  editProfile: {
    getPath: () => `${HOME_PATH}/profile/edit`,
    getBasePath: () => '/profile/edit'
  },
  myMembershipPrograms: {
    getPath: () => `${HOME_PATH}/membership-programs`,
    getBasePath: () => '/membership-programs'
  },
  bookings: {
    getPath: (queryParams) =>
      getUrlWithQueryParams({
        path: `${HOME_PATH}/bookings`,
        queryParams
      }),
    getBasePath: () => '/bookings'
  },
  myReferrals: {
    getPath: () => `${HOME_PATH}/referrals`,
    getBasePath: () => '/referrals'
  },
  fleetCalendar: {
    getPath: () => '/fleet/calendar',
    getBasePath: () => '/fleet/calendar'
  },
  fleetCalendarPublic: {
    getPath: ({ departmentSlug }) => `/fleet/${departmentSlug}/calendar`,
    getBasePath: () => '/fleet/:departmentSlug/calendar'
  },
  brandInsights: {
    getPath: ({ insightsToken }) => `/insights/${encodeURIComponent(insightsToken)}`,
    getBasePath: () => '/insights/:insightsToken'
  },
  fleet: {
    getPath: () => `${HOME_PATH}/fleet`,
    getBasePath: () => '/fleet'
  },
  confirmation: {
    getPath: ({ slug }) => `/confirmation/${slug}`,
    getBasePath: () => '/confirmation/:slug'
  },
  membership: {
    getPath: ({ slug }) => `/memberships/${slug}`,
    getBasePath: () => '/memberships/:slug'
  },
  paymentInfo: {
    getPath: ({ slug }) => `/b/payment/${slug}`,
    getBasePath: () => '/b/payment/:slug'
  },
  referralsInfo: {
    getPath: ({ slug }) => `/referrals/${slug}`,
    getBasePath: () => '/referrals/:slug'
  },
  campaignFromCode: {
    getPath: ({ code }) => `/campaigns/${code}`,
    getBasePath: () => '/campaigns/:code'
  },
  campaignFromCodeLegacy: {
    getPath: ({ code }) => `/campaigns/from-code/${code}`,
    getBasePath: () => '/campaigns/from-code/:code'
  },
  bookingInfo: {
    getPath: ({ slug, ...rest }) =>
      getUrlWithQueryParams({
        path: `/b/${slug}`,
        queryParams: rest
      }),
    getBasePath: () => '/b/:slug'
  },
  bookingUpdateOffer: {
    getPath: ({ slug, ...rest }) =>
      getUrlWithQueryParams({
        path: `/b/${slug}/offer`,
        queryParams: rest
      }),
    getBasePath: () => '/b/:slug/offer'
  },
  changeBooking: {
    getPath: ({ slug }) => `/change/${slug}`,
    getBasePath: () => '/change/:slug'
  },
  editMyBookingTimeWindow: {
    getPath: ({ slug }) => `/change/${slug}/time-window`,
    getBasePath: () => '/change/:slug/time-window'
  },
  editBookingAddress: {
    getPath: ({ slug }) => `/change/${slug}/address`,
    getBasePath: () => '/change/:slug/address'
  },
  newBooking: {
    getPath: () => `${NEW_BOOKING_PATH}`,
    getBasePath: () => `${NEW_BOOKING_PATH}/*`
  },
  /** Mobile vs stationary entry — first step for all brands. */
  newBookingChoice: {
    getPath: () => `${NEW_BOOKING_PATH}/choice`,
    getBasePath: () => `/choice`
  },
  newBookingAddress: {
    getPath: () => `${NEW_BOOKING_PATH}/address`,
    getBasePath: () => `/address`
  },
  newBookingCars: {
    getPath: () => `${NEW_BOOKING_PATH}/cars`,
    getBasePath: () => `/cars`
  },
  newBookingPrimaryItems: {
    getPath: () => `${NEW_BOOKING_PATH}/services`,
    getBasePath: () => `/services`
  },
  newBookingAddons: {
    getPath: () => `${NEW_BOOKING_PATH}/addons`,
    getBasePath: () => '/addons'
  },
  newBookingTireHotelSeason: {
    getPath: () => `${NEW_BOOKING_PATH}/tire-hotel-season`,
    getBasePath: () => '/tire-hotel-season'
  },
  newBookingTireHotel: {
    getPath: () => `${NEW_BOOKING_PATH}/tire-hotel-pickup`,
    getBasePath: () => '/tire-hotel-pickup'
  },
  // style set in homeLayout and routerLayout based on "time-window" in url
  newBookingTimeWindow: {
    getPath: () => `${NEW_BOOKING_PATH}/time-window`,
    getBasePath: () => '/time-window'
  },
  newBookingInsuranceInfo: {
    getPath: () => `${NEW_BOOKING_PATH}/insurance-info`,
    getBasePath: () => '/insurance-info'
  },
  newBookingLogin: {
    getPath: () => `${NEW_BOOKING_PATH}/login`,
    getBasePath: () => '/login'
  },
  newBookingSummary: {
    getPath: () => `${NEW_BOOKING_PATH}/summary`,
    getBasePath: () => '/summary'
  },
  newBookingBuilding: {
    getPath: () => `${NEW_BOOKING_PATH}/building`,
    getBasePath: () => '/building'
  },
  companySignup: {
    getPath: () => '/company-signup',
    getBasePath: () => '/company-signup'
  },
  companySignupSuccess: {
    getPath: () => '/company-signup/success',
    getBasePath: () => '/company-signup/success'
  },
  requestTireOffer: {
    getPath: () => `/request-tire-offer`,
    getBasePath: () => '/request-tire-offer'
  },
  tireOffer: {
    getPath: () => `${TIRE_OFFER_PATH}/*`,
    getBasePath: () => `${TIRE_OFFER_PATH}/*`
  },
  tireOfferSelection: {
    getPath: ({ slug }) => `${TIRE_OFFER_PATH}/select/${slug}`,
    getBasePath: () => `/select/:slug`
  },
  tireOfferAddress: {
    getPath: ({ slug }) => `${TIRE_OFFER_PATH}/address/${slug}`,
    getBasePath: () => `/address/:slug`
  },
  tireOfferLogin: {
    getPath: ({ slug }) => `${TIRE_OFFER_PATH}/login/${slug}`,
    getBasePath: () => `/login/:slug`
  },
  tireOfferSummary: {
    getPath: ({ slug }) => `${TIRE_OFFER_PATH}/summary/${slug}`,
    getBasePath: () => `/summary/:slug`
  },
  tireOfferConfirmation: {
    getPath: ({ sessionId }) => `${TIRE_OFFER_PATH}/confirmation/${sessionId}`,
    getBasePath: () => '/confirmation/:sessionId'
  },
  tireOfferPaymentInfo: {
    getPath: ({ sessionId }) => `${TIRE_OFFER_PATH}/payment-info/${sessionId}`,
    getBasePath: () => '/payment-info/:sessionId'
  },
  tireOfferInvoicedOrderConfirmed: {
    getPath: ({ slug }) => `${TIRE_OFFER_PATH}/invoiced-order-confirmed?tire-quote-slug=${slug}`,
    getBasePath: () => '/invoiced-order-confirmed'
  },
  cancelledBooking: {
    getPath: () => `/bookings/cancelled`,
    getBasePath: () => '/bookings/cancelled'
  },
  cancelledBookingFeedback: {
    getPath: () => `/bookings/cancelled/feedback-completed`,
    getBasePath: () => '/bookings/cancelled/feedback-completed'
  },
  tireOfferStatus: {
    getPath: ({ slug, ...rest }) =>
      getUrlWithQueryParams({
        path: `/t/${slug}`,
        queryParams: rest
      }),
    getBasePath: () => '/t/:slug'
  },
  bookingProposal: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/*`,
    getBasePath: () => `${BOOKING_PROPOSAL_PATH}/:slug/*`
  },
  bookingProposalLandingPage: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}`,
    getBasePath: () => `/`
  },
  bookingProposalAddress: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/address`,
    getBasePath: () => `/address`
  },
  bookingProposalTimeWindowPicker: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/time-window`,
    getBasePath: () => `/time-window`
  },
  bookingProposalLogin: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/login`,
    getBasePath: () => `/login`
  },
  bookingProposalConfirmation: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/confirmation`,
    getBasePath: () => `/confirmation`
  },
  bookingProposalBuilding: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/building`,
    getBasePath: () => `/building`
  },
  stationaryProposalTimeWindow: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/stationary-time-window`,
    getBasePath: () => `/stationary-time-window`
  },
  stationaryProposalLogin: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/stationary-login`,
    getBasePath: () => `/stationary-login`
  },
  stationaryProposalSummary: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/stationary-summary`,
    getBasePath: () => `/stationary-summary`
  },
  editProposalAddress: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/edit-address`,
    getBasePath: () => `/edit-address`
  },
  stationaryBooking: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/*`,
    getBasePath: () => `${STATIONARY_BOOKING_PATH}/*`
  },
  /** @deprecated Use `newBookingChoice` — kept for legacy URL redirects. */
  // oxlint-disable-next-line typescript/no-deprecated -- legacy URL kept as a redirect source
  newStationaryBookingChoice: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/choice`,
    getBasePath: () => `/choice`
  },
  newStationaryBookingCar: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/car`,
    getBasePath: () => `/car`
  },
  newStationaryBookingServices: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/services`,
    getBasePath: () => `/services`
  },
  newStationaryBookingTireSeason: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/tire-season`,
    getBasePath: () => `/tire-season`
  },
  newStationaryBookingTimeWindow: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/time-window`,
    getBasePath: () => `/time-window`
  },
  newStationaryBookingLogin: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/login`,
    getBasePath: () => `/login`
  },
  newStationaryBookingSummary: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/summary`,
    getBasePath: () => `/summary`
  },
  newStationaryBookingPayForBooking: {
    getPath: ({ sessionId }) => `${STATIONARY_BOOKING_PATH}/pay-for-booking/${sessionId}`,
    getBasePath: () => `/pay-for-booking/:sessionId`
  },
  newStationaryBookingConfirmation: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/confirmation`,
    getBasePath: () => `/confirmation`
  },
  newStationaryBookingConfirmationNoPayment: {
    getPath: () => `${STATIONARY_BOOKING_PATH}/confirmation-no-payment`,
    getBasePath: () => `/confirmation-no-payment`
  },
  payForBooking: {
    getPath: ({ sessionId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/pay-for-booking/${sessionId}`,
        queryParams: rest
      }),
    getBasePath: () => `/pay-for-booking/:sessionId`
  },
  feedbackForBooking: {
    getPath: ({ slug }) => `/feedback-for-booking/${slug}`,
    getBasePath: () => `/feedback-for-booking/:slug`
  },
  unableToCompleteDetails: {
    getPath: ({ slug }) => `/b/${slug}/unable-to-complete`,
    getBasePath: () => '/b/:slug/unable-to-complete'
  },
  deliveryMap: {
    getPath: () => '/delivery-map',
    getBasePath: () => '/delivery-map'
  },
  organizationMembers: {
    getPath: () => `${HOME_PATH}/organization-members`,
    getBasePath: () => '/organization-members'
  },
  pointOfSalePayment: {
    getPath: ({ serviceDepartmentId }) => `/payment/${serviceDepartmentId}`,
    getBasePath: () => '/payment/:serviceDepartmentId'
  },
  storageTransferPublic: {
    getPath: ({ slug }) => `/storage-transfer/${slug}`,
    getBasePath: () => '/storage-transfer/:slug'
  },
  storageFacilityTransfers: {
    getPath: ({ publicSlug }) => `/storage-facility/${publicSlug}`,
    getBasePath: () => '/storage-facility/:publicSlug'
  },
  tireHotelRehomePublic: {
    getPath: ({ slug }) => `/tire-hotel-rehome/${slug}`,
    getBasePath: () => '/tire-hotel-rehome/:slug'
  }
});
